import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  NavLink,
  Navigate,
  useNavigate,
  useLocation,
} from 'react-router-dom';
import './App.css';
import { isHostRole } from './utils/roles';
import ModuleViewer from './components/ModuleViewer';
import PracticePage from './components/PracticePage';
import PracticeSession from './components/PracticeSession';
import ResourcesPage from './components/ResourcesPage';
import SettingsPage from './components/SettingsPage';
import PatternPulse from './components/PatternPulse';
import BreathingBreak from './components/BreathingBreak';
import WordOfDay from './components/WordOfDay';
import ManageUsersPage from './components/ManageUsersPage';
import ManageMentorsPage from './components/ManageMentorsPage';
import AdminWorkspacePage from './components/AdminWorkspacePage';
import DashboardPage from './components/dashboard/DashboardPage';
import InsightPanel from './components/dashboard/InsightPanel';
import SetPasswordPage from './components/SetPasswordPage';
import { ThemeProvider } from './contexts/ThemeContext';
import { UserContext, useCurrentUser } from './contexts/UserContext';
import { startSession, joinSession, endSession, heartbeat, getSessionStatus } from './services/zoomService';
import { login as loginRequest } from './services/authService';
import { getAllModules, getModulesForStudent, getModulesForCourse } from './services/moduleService';
import { getStudentEnrollments } from './services/enrollmentService';
import { listCourses } from './services/courseService';
import { useCoursesByRole } from './hooks/useCourses';
import { joinMeeting, leaveMeeting, onConnectionChange, offConnectionChange } from './zoom/client';
import { buildQuestionPool, LENGTH_OPTIONS, DEFAULT_LENGTH_ID } from './data/practiceCatalog';
import ClassroomLKPage from './components/ClassroomLK/ClassroomLKPage';
import JoinPage from './components/JoinPage';
import SatSession from './components/SatSession';
import PythonLab from './components/PythonLab';

// ---------------------------------------------------------------------------
// Stable Zoom container context
//
// The Zoom Component View SDK keeps module-level references to the canvas and
// media workers it initialises during client.init(). If that container div is
// removed from the DOM (e.g. ClassroomPage unmounts on navigation) and a new
// div is passed to init() on the next session, the SDK reuses its old canvas
// references → blank/black panel.
//
// Fix: a single <div> lives permanently in AuthenticatedLayout (never unmounts).
// ClassroomPage adopts it via a DOM move on mount and returns it on unmount.
// The SDK always receives the same DOM node, regardless of navigation.
// ---------------------------------------------------------------------------
const ZoomRootContext = createContext(null);

// ---------------------------------------------------------------------------
// Auth / user configuration
// ---------------------------------------------------------------------------

const STORAGE_KEY = 'minversity-user';

const SEARCH_PROMPT = 'Search assignments, modules, peers...';

const NAV_ITEMS = [
  {
    path: '/dashboard',
    label: 'Dashboard',
    description: 'Review your learning feed and current focus areas.',
    component: DashboardPage,
  },
  {
    path: '/learn',
    label: 'Learn',
    description: 'Browse lessons and track syllabus progress.',
    component: LearnPage,
  },
  {
    path: '/practice',
    label: 'Practice',
    description: 'Complete exercises and mastery checks.',
    component: PracticePage,
  },
  {
    path: '/resources',
    label: 'Resources',
    description: 'Find guides, reference notes, and inspiration.',
    component: ResourcesPage,
  },
  {
    path: '/settings',
    label: 'Settings',
    description: 'Update your profile and app preferences.',
    component: SettingsPage,
  },
  {
    path: '/manage-users',
    label: 'Sales Ops',
    description: 'Invite prospects and add enrollments.',
    component: ManageUsersPage,
    section: 'ops',
    allowedRoles: ['sales', 'admin'],
  },
  {
    path: '/manage-mentors',
    label: 'Mentor Ops',
    description: 'Assign mentors, manage rosters, and add or remove mentor accounts.',
    component: ManageMentorsPage,
    section: 'ops',
    allowedRoles: ['mentor-lead', 'admin'],
  },
  {
    path: '/admin',
    label: 'Admin',
    description: 'Invite and manage platform team members.',
    component: AdminWorkspacePage,
    section: 'ops',
    allowedRoles: ['admin'],
  },
];

const LEARN_SUBJECTS = ['All', 'Math', 'Science', 'English'];

const LEARN_COLUMNS = [
  { id: 'upNext', title: 'Up Next', helper: 'Drag to start' },
  { id: 'current', title: 'Currently Learning', helper: 'Click to open' },
  { id: 'completed', title: 'Completed', helper: 'Click to open' },
];


const STAFF_ROLES = new Set(['teacher', 'mentor-lead', 'sales', 'admin']);

function LearnPage() {
  const navigate = useNavigate();
  const currentUser = useCurrentUser();
  const [subjectFilter, setSubjectFilter] = useState('All');
  const [courseFilter, setCourseFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [modules, setModules] = useState([]);
  const [loadingModules, setLoadingModules] = useState(false);
  const [modulesError, setModulesError] = useState('');
  const [enrolledCourses, setEnrolledCourses] = useState([]);

  const isStaff = STAFF_ROLES.has(currentUser?.role);
  const studentId =
    currentUser?.role === 'student' ? currentUser?.id || currentUser?._id : null;

  useEffect(() => {
    let cancelled = false;
    setLoadingModules(true);
    setModulesError('');

    const fetchModules = isStaff
      ? getAllModules()
      : studentId
        ? getModulesForStudent(studentId)
        : Promise.resolve({ modules: [] });

    fetchModules
      .then((data) => {
        if (cancelled) return;
        setModules(data?.modules || []);
      })
      .catch((err) => {
        if (cancelled) return;
        setModules([]);
        setModulesError(err.message || 'Unable to load modules.');
      })
      .finally(() => {
        if (cancelled) return;
        setLoadingModules(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isStaff, studentId]);

  useEffect(() => {
    let cancelled = false;
    if (isStaff) {
      listCourses()
        .then((data) => {
          if (cancelled) return;
          const courses = (data?.courses || []).map((c) => ({ ...c, id: c._id || c.id }));
          setEnrolledCourses(courses);
        })
        .catch(() => {
          if (cancelled) return;
          setEnrolledCourses([]);
        });
    } else if (studentId) {
      getStudentEnrollments(studentId)
        .then((data) => {
          if (cancelled) return;
          const courses = (data?.enrollments || []).map((e) => e.course).filter(Boolean);
          setEnrolledCourses(courses);
        })
        .catch(() => {
          if (cancelled) return;
          setEnrolledCourses([]);
        });
    } else {
      setEnrolledCourses([]);
    }
    return () => {
      cancelled = true;
    };
  }, [isStaff, studentId]);

  // Reset course selection whenever subject filter changes
  useEffect(() => {
    setCourseFilter('All');
  }, [subjectFilter]);

  const visibleCourses = enrolledCourses.filter(
    (course) => subjectFilter === 'All' || course.subject === subjectFilter
  );

  const normalizedQuery = searchQuery.trim().toLowerCase();
  const filteredModules = modules.filter((module) => {
    const subject = module.subject || 'General';
    const title = module.title || module.name || '';
    const matchesSubject = subjectFilter === 'All' || subject === subjectFilter;
    const matchesQuery = !normalizedQuery || title.toLowerCase().includes(normalizedQuery);
    const matchesCourse = courseFilter === 'All' || String(module.course) === courseFilter;
    return matchesSubject && matchesQuery && matchesCourse;
  });

  return (
    <div className="learn-page">
      <div className="learn-header">
        <div className="subject-controls">
          <p className="eyebrow">Subjects</p>
          <div className="subject-filter" role="tablist" aria-label="Filter by subject">
            {LEARN_SUBJECTS.map((subject) => (
              <button
                key={subject}
                type="button"
                className={`subject-pill ${subjectFilter === subject ? 'active' : ''}`}
                onClick={() => setSubjectFilter(subject)}
                aria-pressed={subjectFilter === subject}
              >
                {subject}
              </button>
            ))}
          </div>
        </div>
        <div className="course-controls">
          <p className="eyebrow">Courses</p>
          <label className="sr-only" htmlFor="course-filter">Filter by course</label>
          <select
            id="course-filter"
            className="course-filter-select"
            value={courseFilter}
            onChange={(e) => setCourseFilter(e.target.value)}
            aria-label="Filter by course"
          >
            <option value="All">All Courses</option>
            {visibleCourses.map((course) => (
              <option key={course.id} value={String(course.id)}>
                {course.title}
              </option>
            ))}
          </select>
        </div>
        <div className="learn-search">
          <label className="sr-only" htmlFor="module-search">Search modules</label>
          <input
            id="module-search"
            type="search"
            placeholder="Find a module..."
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            aria-label="Search modules"
          />
        </div>
      </div>

      {loadingModules && <p className="muted-text">Loading modules…</p>}
      {!loadingModules && modulesError && <p className="error-text">{modulesError}</p>}
      {!loadingModules && !modulesError && modules.length === 0 && (
        <p className="muted-text">
          {studentId ? 'No modules available for your enrolled courses yet.' : isStaff ? 'No modules have been created yet.' : 'Enroll in a course to start learning.'}
        </p>
      )}

      <div className="board-grid">
        {LEARN_COLUMNS.map((column) => {
          const columnEntries = filteredModules.filter(
            (module) => (module.status || 'upNext') === column.id
          );
          return (
            <section key={column.id} className="kanban-column">
              <header className="column-header">
                <div>
                  <p className="column-title">{column.title}</p>
                  <p className="column-subtitle">{column.helper}</p>
                </div>
                <span className="column-badge">{columnEntries.length}</span>
              </header>
              <div className="kanban-list" role="list">
                {columnEntries.length > 0 ? (
                  columnEntries.map((module) => {
                    const moduleId = module._id || module.id;
                    const subject = module.subject || 'General';
                    return (
                      <article
                        key={moduleId}
                        className="module-card"
                        role="button"
                        tabIndex={0}
                        aria-label={`Open ${module.title}`}
                        onClick={() => navigate(`/learn/modules/${moduleId}`)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            navigate(`/learn/modules/${moduleId}`);
                          }
                        }}
                      >
                        <div>
                          <p className="module-title">{module.title}</p>
                          <p className="muted-text">
                            {module.estimatedMinutes ?? '—'} min · {subject}
                          </p>
                        </div>
                        <span
                          className={`module-tag module-${subject.toLowerCase()}`}
                        >
                          {subject}
                        </span>
                      </article>
                    );
                  })
                ) : (
                  <p className="column-empty">Nothing here yet.</p>
                )}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// ClassroomPage — state machine
//
// Phases:
//   idle      — teacher: show "Start Meeting" button
//               student: polling, show "Waiting for teacher"
//   starting  — teacher: API call in flight to create meeting
//   ready     — student: session is live, show "Join Now" button
//   joining   — SDK init + join in progress (both roles)
//   live      — in the meeting (both roles)
//   ended     — session over (both roles)
// ---------------------------------------------------------------------------

function ClassroomPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const currentUser = useCurrentUser();
  const classInfo = location.state?.classInfo || { id: '', title: 'Live Session', detail: '' };
  const isTeacher = currentUser?.role === 'teacher';
  const studentId =
    currentUser?.role === 'student' ? currentUser?.id || currentUser?._id : null;
  const studentParticipant = useMemo(
    () => (classInfo.participants || []).find((p) => p.role === 'student') ?? null,
    [classInfo.participants],
  );
  const mentorParticipant = useMemo(
    () => (classInfo.participants || []).find((p) => p.role === 'mentor') ?? null,
    [classInfo.participants],
  );
  const isHost = isHostRole(currentUser?.role);
  const meetingStudentId = studentParticipant?.userId ? String(studentParticipant.userId) : null;

  const [phase, setPhase] = useState('idle');
  const [sessionId, setSessionId] = useState(null);
  const [credentials, setCredentials] = useState(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [recordingUrl, setRecordingUrl] = useState(null);
  const [showPatternPulse, setShowPatternPulse] = useState(false);
  const [showBreathingBreak, setShowBreathingBreak] = useState(false);
  const [sideModules, setSideModules] = useState([]);
  const [modulesLoading, setModulesLoading] = useState(false);
  const [modulesError, setModulesError] = useState('');
  const [teacherCourses, setTeacherCourses] = useState([]);
  const [teacherCourseLoading, setTeacherCourseLoading] = useState(false);
  const [teacherCourseError, setTeacherCourseError] = useState('');
  const [selectedTeacherCourseId, setSelectedTeacherCourseId] = useState('');
  const [selectedTeacherModuleId, setSelectedTeacherModuleId] = useState('');
  const [homeworkMessage, setHomeworkMessage] = useState('');
  const [studentSideCourses, setStudentSideCourses] = useState([]);
  const [studentSideCourseId, setStudentSideCourseId] = useState('');

  // Stable Zoom container — lives in AuthenticatedLayout, adopted into this
  // component's zoom-embed-wrapper via DOM move so the SDK always gets the
  // same element across navigation cycles.
  const zoomRootRef = useContext(ZoomRootContext);
  const embedWrapperRef = useRef(null);

  useEffect(() => {
    const el = zoomRootRef?.current;
    const wrapper = embedWrapperRef.current;
    if (!el || !wrapper) return;
    console.log('[ClassroomPage] Adopting stable zoom div into embed wrapper');
    el.style.display = '';
    wrapper.insertBefore(el, wrapper.firstChild);
    return () => {
      console.log('[ClassroomPage] Returning stable zoom div to parking spot on unmount');
      el.style.display = 'none';
      const parking = document.getElementById('zoom-parking-spot');
      if (parking) parking.appendChild(el);
    };
  // zoomRootRef is a stable context ref — deps array intentionally empty
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const defaultHomeworkLength = LENGTH_OPTIONS.find((option) => option.id === DEFAULT_LENGTH_ID) || LENGTH_OPTIONS[0];

  // ── Teacher: start a new session ──────────────────────────────────────────
  const handleStart = useCallback(async () => {
    console.log('[ClassroomPage] handleStart() — classId:', classInfo.id, 'user:', currentUser?.name, currentUser?.email);
    setError('');
    setPhase('starting');
    setMessage('Creating meeting…');
    try {
      const data = await startSession({
        classId: classInfo.id,
        userName: currentUser?.name || 'Teacher',
        userEmail: currentUser?.email || '',
      });
      console.log('[ClassroomPage] startSession() response:', { ...data, signature: '[jwt]', zak: data.zak ? '[zak]' : undefined });
      setSessionId(data.sessionId);
      setCredentials(data);
      setPhase('joining');
    } catch (err) {
      console.error('[ClassroomPage] startSession() failed:', err);
      setError(err.message || 'Failed to start session.');
      setPhase('idle');
    }
  }, [classInfo.id, currentUser]);

  // ── Student: join a live session ──────────────────────────────────────────
  const handleJoin = useCallback(async () => {
    setError('');
    setPhase('joining');
    setMessage('Joining…');
    try {
      const data = await joinSession({
        classId: classInfo.id,
        userName: currentUser?.name || 'Student',
        userEmail: currentUser?.email || '',
      });
      setSessionId(data.sessionId);
      setCredentials(data);
    } catch (err) {
      setError(err.message || 'Failed to join session.');
      setPhase('ready'); // back to ready so they can retry
    }
  }, [classInfo.id, currentUser]);

  // ── Leave / end ───────────────────────────────────────────────────────────
  const handleLeave = useCallback(async () => {
    console.log('[ClassroomPage] handleLeave() — isHost:', isHost, 'sessionId:', sessionId);
    setPhase('ended');
    setMessage('Leaving…');
    if (isHost && sessionId) {
      try { await endSession(sessionId); } catch (e) { console.warn('[ClassroomPage] endSession() failed (best-effort):', e.message); }
    }
    await leaveMeeting();
    setMessage('Session ended.');
  }, [isHost, sessionId]);

  const handleAssignHomework = useCallback(() => {
    if (!selectedTeacherModuleId) {
      setHomeworkMessage('Select a module first.');
      return;
    }

    const selectedModule = sideModules.find((mod) => (mod._id || mod.id) === selectedTeacherModuleId);
    const subjectId = selectedModule?.subjectId || selectedModule?.subject || 'math-geometry';
    const questionPool = buildQuestionPool({ subjectId, moduleIds: [selectedTeacherModuleId] });

    if (!questionPool.length) {
      setHomeworkMessage('No question bank entries available for this module yet.');
      return;
    }

    const desired = defaultHomeworkLength?.questions || 12;
    const shuffled = [...questionPool];
    for (let index = shuffled.length - 1; index > 0; index -= 1) {
      const swapIndex = Math.floor(Math.random() * (index + 1));
      [shuffled[index], shuffled[swapIndex]] = [shuffled[swapIndex], shuffled[index]];
    }
    const selection = shuffled.slice(0, Math.min(desired, questionPool.length));
    setHomeworkMessage('');
    navigate('/practice/session', {
      state: {
        quiz: {
          questions: selection,
          generatedAt: new Date().toISOString(),
          subjectId,
          moduleIds: [selectedTeacherModuleId],
          moduleId: selectedTeacherModuleId,
          lengthId: defaultHomeworkLength?.id || DEFAULT_LENGTH_ID,
          requestedCount: desired,
          availableCount: questionPool.length,
        },
      },
    });
  }, [defaultHomeworkLength, navigate, selectedTeacherModuleId, sideModules]);

  // ── SDK join effect — fires when phase becomes 'joining' + credentials set ─
  //
  // BUG NOTE: Do NOT call leaveMeeting() in this cleanup. React runs the cleanup
  // on every dependency change, including when phase changes from 'joining' → 'live'.
  // That would immediately kill the meeting we just joined. Unmount cleanup is
  // handled in the separate effect below.
  const cancelledRef = useRef(false);
  const sdkDimsRef = useRef(null); // { width, height } — Zoom SDK initialized view size, used for responsive scaling

  useEffect(() => {
    const t = Date.now();
    console.log(`[ClassroomPage][${t}] SDK join effect evaluated — phase: ${phase}, hasCredentials: ${!!credentials}, hasContainer: ${!!zoomRootRef.current}`);
    if (phase !== 'joining' || !credentials || !zoomRootRef.current) return;

    cancelledRef.current = false;
    console.log(`[ClassroomPage][${t}] SDK join effect RUNNING — credentials:`, {
      meetingNumber: credentials.meetingNumber,
      hasSignature: !!credentials.signature,
      hasZak: !!credentials.zak,
      webEndpoint: credentials.webEndpoint,
      sdkKey: credentials.sdkKey,
      userName: credentials.userName,
      userEmail: credentials.userEmail,
      sessionId: credentials.sessionId,
    });

    const connHandler = (evt) => {
      console.log(`[ClassroomPage][conn] connection-change event — state: ${evt.state}, reason: ${evt.reason}, cancelled: ${cancelledRef.current}`);
      if (cancelledRef.current) {
        console.log('[ClassroomPage][conn] ignored — effect already cancelled');
        return;
      }
      if (evt.state === 'Closed' || evt.state === 'Fail') {
        console.log(`[ClassroomPage][conn] meeting closed/failed — setting phase → ended`);
        setPhase('ended');
        setMessage('Session ended.');
      }
    };
    onConnectionChange(connHandler);

    const doJoin = async () => {
      setMessage('Connecting to Zoom…');
      try {
        // Wait one animation frame so the browser computes layout after the DOM
        // adoption (zoom div moved from parking spot into wrapper).
        await new Promise(resolve => requestAnimationFrame(resolve));
        const measureRef = embedWrapperRef.current || zoomRootRef.current;
        const rect = measureRef.getBoundingClientRect();
        const sdkW = Math.floor(rect.width) || 800;
        const sdkH = Math.max(Math.floor(rect.height) - 48, 400);
        sdkDimsRef.current = { width: sdkW, height: sdkH };
        console.log(`[ClassroomPage][${t}] doJoin() — container rect:`, rect, `→ sdkDims: ${sdkW}x${sdkH}`);
        await joinMeeting({
          zoomAppRoot: zoomRootRef.current,
          viewWidth: sdkW,
          viewHeight: sdkH,
          sdkKey: credentials.sdkKey,
          signature: credentials.signature,
          meetingNumber: credentials.meetingNumber,
          password: credentials.passcode || '',
          userName: credentials.userName,
          userEmail: credentials.userEmail || '',
          zak: credentials.zak,
          webEndpoint: credentials.webEndpoint,
        });
        console.log(`[ClassroomPage][${t}] joinMeeting() RESOLVED OK — cancelled: ${cancelledRef.current}`);
        if (!cancelledRef.current) {
          console.log(`[ClassroomPage][${t}] Setting phase → live`);
          setPhase('live');
          setMessage('');
        } else {
          console.warn(`[ClassroomPage][${t}] joinMeeting() resolved but effect was already cancelled — NOT setting phase to live`);
        }
      } catch (err) {
        console.error(`[ClassroomPage][${t}] joinMeeting() THREW:`, err?.message, err?.stack || err);
        if (!cancelledRef.current) {
          setError(err?.message || JSON.stringify(err));
          setPhase('idle');
          if (credentials.sessionId) {
            console.log(`[ClassroomPage][${t}] Ending server session ${credentials.sessionId} after failed join`);
            endSession(credentials.sessionId).catch((e) => console.warn('[ClassroomPage] endSession failed:', e.message));
          }
        }
      }
    };

    doJoin();

    return () => {
      console.log(`[ClassroomPage][${t}] SDK join effect CLEANUP — setting cancelledRef=true (phase at cleanup time: ${phase})`);
      cancelledRef.current = true;
      offConnectionChange(connHandler);
      // ⚠️  intentionally NOT calling leaveMeeting() here — that fires on every
      // dep change (including joining→live), which would kill the meeting.
      // The unmount-only effect below handles leaving on component teardown.
    };
  }, [phase, credentials]);

  // ── Unmount-only cleanup — leave the SDK when ClassroomPage unmounts ────────
  useEffect(() => {
    return () => {
      console.log('[ClassroomPage] UNMOUNTING — calling leaveMeeting()');
      leaveMeeting();
    };
  }, []);

  // ── Responsive Zoom scaling — CSS-transform the SDK content to fit container ─
  // The Zoom SDK renders at a fixed pixel size (sdkDimsRef). When the container
  // resizes (panel layout, window resize), a ResizeObserver scales the SDK's root
  // element so the full Zoom UI stays visible without clipping.
  useEffect(() => {
    if (phase !== 'live') return;
    const container = zoomRootRef.current;
    if (!container) return;

    const applyScale = () => {
      const dims = sdkDimsRef.current;
      if (!dims) return;
      const inner = container.firstElementChild;
      if (!inner) return;
      const cW = container.offsetWidth;
      const cH = container.offsetHeight;
      if (cW < 1 || cH < 1) return;
      const scale = Math.min(cW / dims.width, cH / dims.height);
      inner.style.transformOrigin = 'top left';
      inner.style.transform = Math.abs(scale - 1) < 0.005 ? '' : `scale(${scale.toFixed(4)})`;
    };

    const ro = new ResizeObserver(applyScale);
    ro.observe(container);
    applyScale();

    return () => {
      ro.disconnect();
      // Reset transform when leaving live phase
      const inner = zoomRootRef.current?.firstElementChild;
      if (inner) inner.style.transform = '';
    };
  // zoomRootRef and sdkDimsRef are stable refs — intentionally omitted from deps
  }, [phase]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Student status polling (idle phase only) ───────────────────────────────
  useEffect(() => {
    if (isHost || phase !== 'idle' || !classInfo.id) return;

    let cancelled = false;

    const poll = async () => {
      try {
        const status = await getSessionStatus(classInfo.id);
        if (cancelled) return;
        if (status.status === 'live') {
          setPhase('ready');
        } else if (status.recordingUrl) {
          setRecordingUrl(status.recordingUrl);
        }
      } catch { /* network hiccup — retry next interval */ }
    };

    poll();
    const interval = setInterval(poll, 10000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [isHost, phase, classInfo.id]);

  // ── Teacher heartbeat (live phase only) ────────────────────────────────────
  useEffect(() => {
    if (!isHost || phase !== 'live' || !sessionId) return;
    const interval = setInterval(() => {
      heartbeat(sessionId).catch(() => { /* best-effort */ });
    }, 20000);
    return () => clearInterval(interval);
  }, [isHost, phase, sessionId]);

  useEffect(() => {
    let cancelled = false;
    if (!isTeacher) {
      setTeacherCourses([]);
      setTeacherCourseError('');
      setTeacherCourseLoading(false);
      setSelectedTeacherCourseId('');
      return undefined;
    }
    if (!meetingStudentId) {
      setTeacherCourses([]);
      setTeacherCourseLoading(false);
      return undefined;
    }
    setTeacherCourseLoading(true);
    setTeacherCourseError('');
    getStudentEnrollments(meetingStudentId)
      .then((data) => {
        if (cancelled) return;
        const courses = (data?.enrollments || []).map((e) => ({
          _id: String(e.course.id),
          id: String(e.course.id),
          title: e.course.title,
          subject: e.course.subject,
          grade: e.course.grade,
          code: e.course.code,
        }));
        setTeacherCourses(courses);
        setSelectedTeacherCourseId((prev) => prev || (courses[0]?.id || ''));
      })
      .catch((err) => {
        if (cancelled) return;
        setTeacherCourses([]);
        setTeacherCourseError(err.message || 'Unable to load courses.');
      })
      .finally(() => {
        if (cancelled) return;
        setTeacherCourseLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isTeacher, meetingStudentId]);

  useEffect(() => {
    if (!isTeacher) return undefined;
    let cancelled = false;
    if (!selectedTeacherCourseId) {
      setSideModules([]);
      setModulesError('');
      setModulesLoading(false);
      setSelectedTeacherModuleId('');
      return undefined;
    }
    setModulesLoading(true);
    setModulesError('');
    getModulesForCourse(selectedTeacherCourseId)
      .then((data) => {
        if (cancelled) return;
        const modules = data?.modules || [];
        setSideModules(modules);
        setSelectedTeacherModuleId((prev) => {
          if (prev && modules.some((mod) => (mod._id || mod.id) === prev)) {
            return prev;
          }
          return modules[0]?._id || modules[0]?.id || '';
        });
      })
      .catch((err) => {
        if (cancelled) return;
        setSideModules([]);
        setModulesError(err.message || 'Unable to load modules.');
        setSelectedTeacherModuleId('');
      })
      .finally(() => {
        if (cancelled) return;
        setModulesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isTeacher, selectedTeacherCourseId]);

  useEffect(() => {
    setHomeworkMessage('');
  }, [selectedTeacherCourseId, selectedTeacherModuleId]);

  useEffect(() => {
    if (isTeacher) return undefined;
    let cancelled = false;
    if (!studentId) {
      setSideModules([]);
      setModulesError('');
      setModulesLoading(false);
      return undefined;
    }
    setModulesLoading(true);
    setModulesError('');
    getModulesForStudent(studentId)
      .then((data) => {
        if (cancelled) return;
        setSideModules(data?.modules || []);
      })
      .catch((err) => {
        if (cancelled) return;
        setSideModules([]);
        setModulesError(err.message || 'Unable to load modules.');
      })
      .finally(() => {
        if (cancelled) return;
        setModulesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isTeacher, studentId]);

  useEffect(() => {
    let cancelled = false;
    if (isTeacher || !studentId) {
      setStudentSideCourses([]);
      setStudentSideCourseId('');
      return undefined;
    }
    getStudentEnrollments(studentId)
      .then((data) => {
        if (cancelled) return;
        const courses = (data?.enrollments || []).map((e) => ({
          id: String(e.course.id),
          title: e.course.title,
          grade: e.course.grade,
        }));
        setStudentSideCourses(courses);
      })
      .catch(() => { if (!cancelled) setStudentSideCourses([]); });
    return () => { cancelled = true; };
  }, [isTeacher, studentId]);

  // ── sendBeacon on tab close (teacher only) ─────────────────────────────────
  useEffect(() => {
    if (!isHost || phase !== 'live' || !sessionId) return;
    const apiBase = process.env.REACT_APP_ZOOM_API_BASE || '';
    const handleUnload = () => {
      navigator.sendBeacon(
        `${apiBase}/api/sessions/end`,
        JSON.stringify({ sessionId })
      );
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, [isHost, phase, sessionId]);

  // ── Render helpers ─────────────────────────────────────────────────────────

  const visibleSideModules = sideModules.filter(
    (mod) => !studentSideCourseId || String(mod.course) === studentSideCourseId,
  );

  const renderOverlay = () => {
    if (phase === 'live') return null;

    return (
      <div className="session-overlay">
        {phase === 'idle' && isHost && (
          <>
            <p className="overlay-label">Ready to begin?</p>
            <button type="button" className="classroom-action-btn classroom-action-btn--inline" onClick={handleStart}>
              Start Meeting
            </button>
          </>
        )}
        {phase === 'starting' && (
          <p className="overlay-label">{message || 'Creating meeting…'}</p>
        )}
        {phase === 'idle' && !isHost && (
          <>
            <p className="overlay-label">Waiting for teacher to start the class…</p>
            <p className="muted-text overlay-sub">This page checks automatically every 10 seconds.</p>
          </>
        )}
        {phase === 'ready' && !isHost && (
          <>
            <p className="overlay-label">Teacher has started the class.</p>
            <button type="button" className="primary-btn" onClick={handleJoin}>
              Join Now
            </button>
          </>
        )}
        {phase === 'joining' && (
          <p className="overlay-label">{message || 'Connecting…'}</p>
        )}
        {phase === 'ended' && (
          <>
            <p className="overlay-label">{message || 'Session ended.'}</p>
            {recordingUrl && (
              <a href={recordingUrl} target="_blank" rel="noopener noreferrer" className="primary-btn">
                Watch Recording
              </a>
            )}
          </>
        )}
        {error && <p className="error-text overlay-error">{error}</p>}
      </div>
    );
  };

  return (
    <div className="classroom-stage">
      <div className="classroom-header">
        <div>
          <p className="eyebrow">Live classroom</p>
          <h2>{classInfo.title}</h2>
          <p className="muted-text">{classInfo.detail}</p>
        </div>
        <div className="classroom-header-right">
          <div className="classroom-profile-cards">
            {[
              { label: 'Student', participant: studentParticipant },
              { label: 'Mentor', participant: mentorParticipant },
            ].map(({ label, participant }) => (
              <article key={label} className="profile-card">
                <p className="profile-label">{label}</p>
                <p className="profile-name">{participant?.name ?? '—'}</p>
              </article>
            ))}
          </div>
          {phase === 'live' && (
            <button type="button" className="secondary-btn compact" onClick={handleLeave}>
              {isHost ? 'Leave & End' : 'Leave'}
            </button>
          )}
        </div>
      </div>

      <div className="classroom-body-row">
        <div className="zoom-pane">
          {/* embedWrapperRef: stable Zoom div is DOM-moved into here on mount */}
          <div className="zoom-embed-wrapper" ref={embedWrapperRef}>
            {/* Overlay covers the container when not live */}
            {renderOverlay()}
          </div>
        </div>

        <aside className="classroom-side-panel">
          <div className="side-panel-section">
            <p className="eyebrow side-section-label">Focus &amp; Wellness</p>
            <div className="side-wellness-tiles">
              <button type="button" className="side-wellness-tile" onClick={() => setShowPatternPulse(true)}>
                <span className="tile-accent" aria-hidden="true">🧠</span>
                <p className="tile-title">Pattern Pulse</p>
                <p className="tile-tag">Boost working memory</p>
              </button>
              <button type="button" className="side-wellness-tile" onClick={() => setShowBreathingBreak(true)}>
                <span className="tile-accent" aria-hidden="true">🌿</span>
                <p className="tile-title">Wellness Break</p>
                <p className="tile-tag">Guided breathing</p>
              </button>
            </div>
          </div>

          <div className="side-panel-section side-modules-section">
            <p className="eyebrow side-section-label">Learning Modules</p>
            {isTeacher ? (
              <div className="teacher-module-controls">
                {teacherCourseLoading && <p className="muted-text">Loading courses…</p>}
                {!teacherCourseLoading && teacherCourseError && <p className="error-text">{teacherCourseError}</p>}
                {!teacherCourseLoading && !teacherCourseError && teacherCourses.length === 0 && (
                  <p className="muted-text">No courses available yet.</p>
                )}
                {!teacherCourseLoading && !teacherCourseError && teacherCourses.length > 0 && (
                  <>
                    <div className="side-control-row">
                      <label className="eyebrow" htmlFor="teacher-course-select">Course</label>
                      <select
                        id="teacher-course-select"
                        className="side-select"
                        value={selectedTeacherCourseId}
                        onChange={(event) => {
                          setSelectedTeacherCourseId(event.target.value);
                          setSelectedTeacherModuleId('');
                        }}
                      >
                        {teacherCourses.map((course) => {
                          const courseId = course._id || course.id;
                          const subtitle = course.grade
                            ? `Grade ${course.grade} · ${course.subject || ''}`
                            : course.subject || '';
                          return (
                            <option key={courseId} value={courseId}>
                              {course.title || course.code || 'Course'}{subtitle ? ` — ${subtitle}` : ''}
                            </option>
                          );
                        })}
                      </select>
                    </div>

                    <div className="side-control-row">
                      <label className="eyebrow" htmlFor="teacher-module-select">Module</label>
                      <select
                        id="teacher-module-select"
                        className="side-select"
                        value={selectedTeacherModuleId}
                        onChange={(event) => setSelectedTeacherModuleId(event.target.value)}
                        disabled={modulesLoading || sideModules.length === 0}
                      >
                        {sideModules.map((mod) => {
                          const moduleId = mod._id || mod.id;
                          return (
                            <option key={moduleId} value={moduleId}>
                              {mod.title}
                            </option>
                          );
                        })}
                      </select>
                    </div>

                    {modulesLoading && <p className="muted-text">Loading modules…</p>}
                    {!modulesLoading && modulesError && <p className="error-text">{modulesError}</p>}
                    {!modulesLoading && !modulesError && sideModules.length === 0 && (
                      <p className="muted-text">No modules for this course yet.</p>
                    )}

                    <div className="classroom-action-btns">
                      <button
                        type="button"
                        className="classroom-action-btn"
                        disabled={!selectedTeacherModuleId}
                        onClick={() => {
                          if (selectedTeacherModuleId) {
                            window.open(`/learn/modules/${selectedTeacherModuleId}`, '_blank');
                          }
                        }}
                      >
                        Open Module
                      </button>
                      <button
                        type="button"
                        className="classroom-action-btn"
                        disabled={!selectedTeacherModuleId}
                        onClick={handleAssignHomework}
                      >
                        Assign Quiz
                      </button>
                      <button
                        type="button"
                        className="classroom-action-btn"
                        onClick={() => window.open('/resources', '_blank')}
                      >
                        Share Resources
                      </button>
                    </div>
                    {homeworkMessage && <p className="muted-text">{homeworkMessage}</p>}
                  </>
                )}
              </div>
            ) : (
              <div className="side-module-list">
                {studentSideCourses.length > 0 && (
                  <>
                    <div className="side-control-row">
                      <label className="eyebrow" htmlFor="student-course-select">Course</label>
                      <select
                        id="student-course-select"
                        className="side-select"
                        value={studentSideCourseId}
                        onChange={(e) => setStudentSideCourseId(e.target.value)}
                      >
                        <option value="">All Courses</option>
                        {studentSideCourses.map((course) => (
                          <option key={course.id} value={course.id}>
                            {course.title}{course.grade ? ` — Grade ${course.grade}` : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                  </>
                )}
                {modulesLoading && <p className="muted-text">Loading modules…</p>}
                {!modulesLoading && modulesError && <p className="error-text">{modulesError}</p>}
                {!modulesLoading && !modulesError && visibleSideModules.length === 0 && (
                  <p className="muted-text">
                    {studentId ? 'No modules linked to your courses yet.' : 'Enroll in a course to unlock modules.'}
                  </p>
                )}
                {!modulesLoading &&
                  !modulesError &&
                  visibleSideModules.map((mod) => {
                    const moduleId = mod._id || mod.id;
                    return (
                      <button
                        key={moduleId}
                        type="button"
                        className="side-module-item"
                        onClick={() => window.open(`/learn/modules/${moduleId}`, '_blank')}
                      >
                        <div className="side-module-info">
                          <p className="side-module-title">{mod.title}</p>
                          <p className="side-module-meta">
                            {(mod.subject || 'General')} · {mod.estimatedMinutes || '—'} min
                          </p>
                        </div>
                        <svg className="side-module-arrow" viewBox="0 0 16 16" aria-hidden="true">
                          <path d="M6 12l4-4-4-4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                    );
                  })}
              </div>
            )}
          </div>
        </aside>
      </div>

      {showPatternPulse && <PatternPulse onClose={() => setShowPatternPulse(false)} />}
      {showBreathingBreak && <BreathingBreak onClose={() => setShowBreathingBreak(false)} />}
    </div>
  );
}

// Redirects to /dashboard if the current user's role is not in allowedRoles.
function ProtectedRoute({ allowedRoles, children }) {
  const currentUser = useCurrentUser();
  if (allowedRoles && !allowedRoles.includes(currentUser?.role)) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function PlaceholderPage({ title, description }) {
  return (
    <div className="page-placeholder">
      <div className="placeholder-card">
        <h2>{title}</h2>
        <p className="muted-text">{description}</p>
        <div className="empty-panel">This space unlocks as new student content drops.</div>
      </div>
    </div>
  );
}

function AuthenticatedLayout({ handleSignOut }) {
  const location = useLocation();
  const navigate = useNavigate();
  const currentUser = useCurrentUser();
  const isClassroomView = location.pathname.startsWith('/classroom');
  const isLKClassroomView = location.pathname === '/classroom-lk';
  const isSatExam = location.pathname === '/practice/sat-session';
  const isPracticeSession = location.pathname === '/practice/session' || location.pathname === '/practice/sat-session';
  // Stable Zoom container ref — see ZoomRootContext comment above
  const zoomRootRef = useRef(null);
  const presenterParam = new URLSearchParams(location.search).get('presenter');
  const isPresenterView = location.pathname.startsWith('/learn/modules') && presenterParam === '1';
  const isLearnView = location.pathname.startsWith('/learn');
  const isDashboardView = location.pathname === '/dashboard' || location.pathname === '/';
  const showInsightPanel = isDashboardView && !isPresenterView;
  const hideSidebar = isClassroomView || isPresenterView || isSatExam;
  const visibleNavItems = useMemo(
    () =>
      NAV_ITEMS.filter(
        (item) => !item.allowedRoles || item.allowedRoles.includes(currentUser?.role)
      ),
    [currentUser]
  );

  return (
    <ZoomRootContext.Provider value={zoomRootRef}>
    {/* Parking spot — keeps the Zoom container div alive when ClassroomPage is not mounted */}
    <div id="zoom-parking-spot" style={{ display: 'none' }}>
      <div ref={zoomRootRef} className="zoom-meeting-embed" />
    </div>
    <div className={`workspace-shell ${isClassroomView ? 'classroom-view' : ''} ${isPresenterView ? 'presenter-view' : ''}`}>
      {!isLKClassroomView && !isSatExam && <header className={`top-bar ${isClassroomView ? 'classroom-top-bar' : ''}`}>
        <div className="brand-cluster" aria-label="Minversity brand">
          <span className="brand-icon brand-icon-square" aria-hidden="true">M</span>
          <p className="brand-title">Minversity</p>
        </div>

        <div className={`search-bar ${isClassroomView || isPresenterView ? 'search-bar-hidden' : ''}`}>
          {!isClassroomView && !isPresenterView && (
            <input
              type="search"
              placeholder={SEARCH_PROMPT}
              aria-label="Search assignments, modules, peers"
            />
          )}
        </div>

        <div className="top-bar-right">
          <div className="user-cluster">
            <div className="user-meta">
              <p className="user-name">{currentUser?.name || ''}</p>
            </div>
            {isClassroomView && !isLKClassroomView ? (
              <button type="button" className="secondary-btn compact" onClick={() => navigate('/dashboard')}>
                Exit classroom
              </button>
            ) : !isLKClassroomView ? (
              <button
                type="button"
                className="secondary-btn compact icon-only"
                onClick={handleSignOut}
                aria-label="Sign out"
                title="Sign out"
              >
                <svg
                  className="icon-signout"
                  viewBox="0 0 24 24"
                  role="img"
                  aria-hidden="true"
                  focusable="false"
                >
                  <path
                    d="M14.75 4.5h-2.25c-1.573 0-2.36 0-2.964.306a2.5 2.5 0 0 0-1.09 1.09C8.14 6.5 8.14 7.287 8.14 8.86v6.28c0 1.573 0 2.36.306 2.964.259.461.629.831 1.09 1.09.604.306 1.391.306 2.964.306h2.25"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                  />
                  <path
                    d="M11.5 12h8m0 0-3-3m3 3-3 3"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            ) : null}
          </div>
        </div>
      </header>}

      <div className={`workspace-body ${isClassroomView ? 'classroom-body' : ''} ${isPresenterView ? 'presenter-body' : ''}`}>
        {!hideSidebar && (
          <nav className="sidebar" aria-label="Primary">
            {visibleNavItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        )}

        <div className={`main-layout ${isClassroomView ? 'classroom-layout' : ''}`}>
          <section
            className={`content-area ${isClassroomView ? 'classroom-content' : ''} ${
              isLearnView ? 'learn-content' : ''
            } ${isDashboardView ? 'dashboard-main' : ''} ${isPracticeSession ? 'practice-session-content' : ''}`}
            role="main"
          >
            <Routes>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              {NAV_ITEMS.map((item) => {
                const Component = item.component;
                const element = Component ? (
                  <Component />
                ) : (
                  <PlaceholderPage title={item.label} description={item.description} />
                );
                return (
                  <Route
                    key={item.path}
                    path={item.path}
                    element={
                      item.allowedRoles ? (
                        <ProtectedRoute allowedRoles={item.allowedRoles}>
                          {element}
                        </ProtectedRoute>
                      ) : element
                    }
                  />
                );
              })}
              <Route path="/practice/session" element={<PracticeSession />} />
              <Route path="/practice/sat-session" element={<SatSession />} />
              <Route path="/classroom" element={<ClassroomPage />} />
              <Route path="/classroom-lk" element={<ClassroomLKPage />} />
              <Route path="/learn/modules/:moduleId" element={<ModuleViewer />} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </section>

          {showInsightPanel && (
            <aside className="insight-panel" aria-label="Learning insights">
              <InsightPanel />
            </aside>
          )}
        </div>
      </div>

      {!isClassroomView && !isPresenterView && !isSatExam && (
        <footer className="app-footer">
          <span>© 2026 Riva Learning Platforms</span>
          <span>v4.1.2</span>
          <span>Terms · Privacy</span>
        </footer>
      )}
    </div>
    </ZoomRootContext.Provider>
  );
}

function LoginForm({ onLogin }) {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const returnPath = new URLSearchParams(window.location.search).get('return') || '/dashboard';

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      setError('');
      const { user } = await loginRequest({
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
      });
      onLogin(user);
      // Navigate to return path (e.g. /join/:token) after login
      if (returnPath && returnPath !== '/dashboard') {
        window.location.replace(returnPath);
      }
    } catch (err) {
      setError(err.message || 'Invalid credentials.');
    }
  };

  return (
    <div className="auth-shell">
      <div className="backdrop" aria-hidden="true" />
      <main className="auth-wrapper">
        <div className="auth-card" role="dialog" aria-modal="true" aria-labelledby="auth-title">
          <div className="brand-pill">
            <span className="brand-icon">M</span>
            <span className="brand-name">Minversity</span>
          </div>
          <h1 id="auth-title">Sign in</h1>
          <p className="subtitle">Access your learning workspace</p>

          <form className="auth-form" onSubmit={handleSubmit}>
            <label className="field-label" htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="demo@minversity.edu"
              autoComplete="username"
              required
            />

            <label className="field-label" htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="••••••"
              autoComplete="current-password"
              required
            />

            {error && <p className="error-text" role="alert">{error}</p>}

            <button type="submit" className="primary-btn">Sign in</button>
          </form>
        </div>
      </main>
    </div>
  );
}

function App() {
  const [currentUser, setCurrentUser] = useState(null);

  // Restore session from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) setCurrentUser(JSON.parse(stored));
    } catch { /* stale / corrupt entry — ignore */ }
  }, []);

  const handleLogin = (user) => {
    setCurrentUser(user);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
  };

  const handleSignOut = () => {
    setCurrentUser(null);
    localStorage.removeItem(STORAGE_KEY);
  };

  return (
    <ThemeProvider>
      <Router>
        <Routes>
          {/* Public routes — accessible without auth */}
          <Route
            path="/welcome"
            element={<SetPasswordPage onActivated={handleLogin} />}
          />
          <Route
            path="/join/:token"
            element={
              <UserContext.Provider value={currentUser}>
                <JoinPage />
              </UserContext.Provider>
            }
          />
          <Route
            path="/classroom-lk"
            element={
              <UserContext.Provider value={currentUser}>
                <ClassroomLKPage />
              </UserContext.Provider>
            }
          />
          <Route
            path="/python/:lessonId"
            element={
              <UserContext.Provider value={currentUser}>
                <PythonLab />
              </UserContext.Provider>
            }
          />

          {/* All other routes — require auth */}
          <Route
            path="*"
            element={
              currentUser ? (
                <UserContext.Provider value={currentUser}>
                  <AuthenticatedLayout handleSignOut={handleSignOut} />
                </UserContext.Provider>
              ) : (
                <LoginForm onLogin={handleLogin} />
              )
            }
          />
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

export default App;
